/**
 * 登录页：校验账号并取回令牌，然后进教室或进管理后台。
 *
 * 老系统的 r.jsp 是把权限直接写进网页再交给 ActiveX 自报的，
 * 改一下网页源码就能拿主持人权限。这里改成服务端签发令牌。
 */

import { useState, type FormEvent, useEffect } from 'react'

import { LineBadge } from '../components/LineBadge'
import { LocalNode } from '../components/LocalNode'
import { isDesktop } from '../lib/desktop'
import { apiURL } from '../lib/endpoint'
import { autoSelect } from '../lib/lineSelect'
import type { ICEServerConf } from '../lib/messages'
import { currentServer } from '../lib/servers'
import { useLine } from '../lib/useLine'

export interface LoginResult {
  token: string
  account: string
  nick: string
  prop: number
  isAdmin: boolean
  roomId: number
  /** 最初进的主房间号。分组讨论换到子房间后，roomId 会变成子房间号 */
  mainRoomId?: number
  /** 房间名。服务端在登录应答里带出来，给准备页显示；可能为空。 */
  roomName: string
  /** 进房密码。房间未设密码时为空串。 */
  roomPassword: string
  /** 该房间开了「成员入会时静音」：准备页的麦克风默认关掉。 */
  muteOnJoin?: boolean
  iceServers: ICEServerConf[]
}

interface Props {
  onDone: (r: LoginResult, target: 'classroom' | 'admin') => void
}

/** 演示账号。仅在服务端明确开启演示模式时才显示，见下方 demoEnabled。
 *
 * 全部指向同一间教室。这几个按钮存在的意义就是「点两下就能两个人对上话」，
 * 各自带不同房间号的话，点完发现彼此看不见 —— 而房间号那一栏被自动填过，
 * 没人会想到去核对它。 */
const DEMO_ROOM = '16908291'

const DEMO_ACCOUNTS: ReadonlyArray<{
  label: string
  account: string
  password: string
}> = [
  { label: '教师 王建国', account: 't01', password: 'Teacher@2026' },
  { label: '教师 李秀兰', account: 't02', password: 'Teacher@2026' },
  { label: '学员 张明', account: 's001', password: 'Student@2026' },
  { label: '学员 刘芳', account: 's002', password: 'Student@2026' },
  { label: '旁听访客', account: 'g001', password: 'Guest@2026' },
]

/**
 * 开发/测试用的默认值。
 *
 * 只在服务端开着演示模式（healthz.demo === true）时才填进去 ——
 * 打包给客户的应用绝不能自带账号口令。
 */
const DEV_DEFAULTS = {
  account: 't01',
  password: 'Teacher@2026',
  roomId: '16908291',
}

export function Login({ onDone }: Props) {
  const [account, setAccount] = useState('')
  const [password, setPassword] = useState('')
  // 邀请链接带 ?room=房间号，打开就填好，别让人再抄一遍。
  const [roomId, setRoomId] = useState(() => {
    const q = new URLSearchParams(location.search).get('room') ?? ''
    return /^\d+$/.test(q) ? q : ''
  })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [mode, setMode] = useState<'classroom' | 'admin'>('classroom')
  // 桌面端 / App 不让用户填服务器地址：内置线路表自动选线（腾讯会议也从不问这个）。
  // 这里只是把线路状态拿来显示、出问题时能手动切。
  const line = useLine()
  // 有些房间设了进房密码。先按房间号问一下服务端要不要密码，
  // 免得用户填完账号密码才发现进不去却又没地方补。
  const [roomPassword, setRoomPassword] = useState('')
  const [needRoomPassword, setNeedRoomPassword] = useState(false)
  // 服务端是否开着演示模式。演示账号的口令是公开的，
  // 所以这排快捷按钮必须由服务端说了算 —— 不能只看前端自己的判断，
  // 否则打包出去的应用在任何环境都会亮出这几个账号。
  const [demoEnabled, setDemoEnabled] = useState(false)

  useEffect(() => {
    let alive = true
    fetch(apiURL('/healthz'))
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (alive && d && d.demo === true) {
          setDemoEnabled(true)
          // 演示模式下把常用值填好，省得每次手打。只填空着的字段，
          // 别覆盖用户或邀请链接已经带进来的值。
          setAccount((v) => v || DEV_DEFAULTS.account)
          setPassword((v) => v || DEV_DEFAULTS.password)
          setRoomId((v) => v || DEV_DEFAULTS.roomId)
        }
      })
      .catch(() => {
        /* 探不到就当没开，不影响正常登录 */
      })
    return () => {
      alive = false
    }
  }, [])

  /** 房间号填好后探一下这个房间要不要密码。 */
  const probeRoom = async (rid: string) => {
    const n = Number(rid)
    if (!Number.isFinite(n) || n <= 0) return
    try {
      const resp = await fetch(apiURL(`/api/rooms/${n}`))
      if (!resp.ok) return
      const info = await resp.json()
      setNeedRoomPassword(!!info.needPassword)
    } catch {
      // 探测失败不打扰用户：真需要密码时，进房失败后仍会把输入框显示出来。
    }
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setErr('')

    const rid = Number(roomId)
    if (mode === 'classroom' && (!Number.isFinite(rid) || rid <= 0)) {
      setErr('请输入有效的房间号')
      return
    }

    // 登录请求。桌面端当前线路连不上时，立刻换一条再试一次 ——
    // 用户点的是「进入教室」，不该由他来操心哪台服务器活着。
    const post = () =>
      fetch(apiURL('/api/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account, password, roomId: mode === 'classroom' ? rid : 0 }),
      })
    const postWithFailover = async () => {
      try {
        return await post()
      } catch (e) {
        if (!isDesktop()) throw e
        const r = await autoSelect(currentServer().id)
        if (!r.server) throw e
        await line.refresh()
        return await post()
      }
    }

    setBusy(true)
    try {
      const resp = await postWithFailover()
      const body = await resp.json()
      if (!resp.ok) {
        setErr(body?.msg ?? '登录失败')
        return
      }
      if (mode === 'admin' && !body.isAdmin) {
        setErr('该账号没有管理后台权限')
        return
      }
      onDone(
        {
          token: body.token,
          account: body.account,
          nick: body.nick,
          prop: body.prop,
          isAdmin: !!body.isAdmin,
          roomId: rid || 0,
          roomName: body.roomName ?? '',
          roomPassword,
          // 用登录响应里的值，不用 probeRoom 探来的：探测挂在房间号输入框的 onBlur 上，
          // 用户直接回车提交时不会触发，于是入会静音时开时不开。
          muteOnJoin: !!body.muteOnJoin,
          iceServers: [],
        },
        mode,
      )
    } catch {
      setErr(isDesktop() ? '所有线路都连不上，请检查网络后重试' : '无法连接服务器，请检查网络')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="login-page">
      <form className="login-card" onSubmit={submit}>
        {/* 品牌区：一个标记加一行副题。
            光有一行黑字标题的登录页，和内网自建工具没有区别 ——
            这是用户见到产品的第一屏，也是唯一一屏纯粹留给「这是什么」的地方。 */}
        <div className="login-brand">
          <span className="login-logo" aria-hidden>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
              <rect x="3.2" y="14.4" width="4" height="5.2" rx="1" />
              <rect x="8.6" y="11.4" width="4" height="8.2" rx="1" />
              <rect x="14" y="8.4" width="4" height="11.2" rx="1" />
              <circle cx="16" cy="5.4" r="3" />
              <path d="M15.1 4.1l2.2 1.3-2.2 1.3z" fill="currentColor" stroke="none" />
            </svg>
          </span>
          <div>
            <h1>网梯直播课堂</h1>
            <p className="login-slogan">在线互动教学平台</p>
          </div>
        </div>

        <div className="login-tabs">
          <button
            type="button"
            className={mode === 'classroom' ? 'on' : ''}
            onClick={() => {
              setMode('classroom')
              setErr('')
            }}
          >
            进入教室
          </button>
          <button
            type="button"
            className={mode === 'admin' ? 'on' : ''}
            onClick={() => {
              setMode('admin')
              setErr('')
            }}
          >
            管理后台
          </button>
        </div>

        <label>
          <span>账号</span>
          <input
            value={account}
            placeholder="请输入账号"
            autoComplete="username"
            onChange={(e) => setAccount(e.target.value)}
            required
          />
        </label>

        <label>
          <span>密码</span>
          <input
            type="password"
            value={password}
            placeholder="请输入密码"
            autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>

        {mode === 'classroom' && (
          <>
            <label>
              <span>房间号</span>
              <input
                value={roomId}
                inputMode="numeric"
                placeholder="例如 16908291"
                onChange={(e) => setRoomId(e.target.value)}
                onBlur={(e) => void probeRoom(e.target.value)}
                required
              />
            </label>

            {needRoomPassword && (
              <label>
                <span>进房密码</span>
                <input
                  type="password"
                  value={roomPassword}
                  placeholder="该房间需要密码"
                  onChange={(e) => setRoomPassword(e.target.value)}
                  required
                />
              </label>
            )}
          </>
        )}

        {demoEnabled && mode === 'classroom' && (
          <div className="demo-accounts">
            <span className="demo-hint">
              演示账号（点一下自动填好，都进 {DEMO_ROOM} 号教室）
            </span>
            <div className="demo-row">
              {DEMO_ACCOUNTS.map((d) => (
                <button
                  key={d.account}
                  type="button"
                  className="demo-btn"
                  onClick={() => {
                    setAccount(d.account)
                    setPassword(d.password)
                    setRoomId(DEMO_ROOM)
                    setErr('')
                  }}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {err && <div className="login-err">{err}</div>}

        <button className="primary block" disabled={busy} type="submit">
          {busy ? '正在进入…' : mode === 'admin' ? '进入管理后台' : '进入教室'}
        </button>

        <LineBadge line={line} />
        {/* 只在线路全断时露出来。网络正常时显示它会诱导用户开多余的节点。 */}
        <LocalNode visible={line.allDown} />
      </form>
    </div>
  )
}
